import { useState } from "react";
import type { ChangeEvent } from "react";
import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EMPTY_VALUE } from "@/constants";
import { orEmptyValue } from "@/lib/hcbb-cost-estimator-utils";
import { HcbbTablePagination } from "@/pages/components/hcbb-cost-estimator/hcbb-table-pagination";
import { HcbbTruncatedText } from "@/pages/components/hcbb-cost-estimator/hcbb-truncated-text";
import type { HcbbOffice } from "@/model/hcbb-cost-estimator";

const OFFICES_HEADING_ID = "hcbb-provider-offices-heading";
const OFFICES_SEARCH_ID = "hcbb-provider-offices-search";

/** Rows per page — the table pages client-side, so this is the whole page size. */
const OFFICES_PAGE_SIZE = 5;

/** Below this many letters, a term is still being typed rather than searched. */
const MIN_SEARCH_LENGTH = 3;

/**
 * Every location this provider practices at, from the professional-details
 * endpoint's own `offices` list.
 *
 * Hidden outright rather than shown empty: a provider with no offices on file
 * still has the address on the card above, so an empty table under it would
 * only read as a second fact contradicting the first.
 */
export const HcbbProviderOffices = ({ offices }: { offices: HcbbOffice[] }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  if (offices.length === 0) return null;

  // A term shorter than the minimum is still being typed, so it matches
  // everything rather than nothing — the list would otherwise empty out on
  // the first keystroke or two of every search.
  const query = search.trim().toLowerCase();
  const filteredOffices =
    query.length < MIN_SEARCH_LENGTH
      ? offices
      : offices.filter((office) =>
          office.displayName.toLowerCase().includes(query),
        );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOffices.length / OFFICES_PAGE_SIZE),
  );
  // Clamped rather than reset: a page a shorter list no longer has still
  // shows its last page instead of silently jumping back to the first.
  const currentPage = Math.min(page, totalPages);
  const visibleOffices = filteredOffices.slice(
    (currentPage - 1) * OFFICES_PAGE_SIZE,
    currentPage * OFFICES_PAGE_SIZE,
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    // A new term reshapes the list, so page 2 of the old one would either
    // land past the end of the new one or show the wrong rows outright.
    setPage(1);
  };

  return (
    <section aria-labelledby={OFFICES_HEADING_ID} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id={OFFICES_HEADING_ID}
          className="text-sm font-medium text-gray-800"
        >
          Facilities this provider practices at:
        </h2>

        <div className="relative w-full max-w-56">
          <label htmlFor={OFFICES_SEARCH_ID} className="sr-only">
            Search facilities by name
          </label>
          <Input
            id={OFFICES_SEARCH_ID}
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search"
            className="pr-9 text-sm"
          />
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-3 my-auto size-4 text-muted-foreground"
          />
        </div>
      </div>

      {filteredOffices.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-600">
          No facilities match &quot;{search.trim()}&quot;.
        </p>
      ) : (
        <>
          <Table aria-labelledby={OFFICES_HEADING_ID}>
            <TableHeader className="uppercase">
              <TableRow className="bg-primary hover:bg-primary">
                <TableHead scope="col" className="text-primary-foreground">
                  Facility Location
                </TableHead>
                <TableHead scope="col" className="text-primary-foreground">
                  Network
                </TableHead>
                <TableHead scope="col" className="text-primary-foreground">
                  Distance
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleOffices.map((office) => (
                <HcbbOfficeRow key={office.id} office={office} />
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <HcbbTablePagination
              label="Facilities pagination"
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </section>
  );
};

/**
 * `networkName` and `tier` are read together as one label — HCBB has said
 * they are coming as a pair ("Cofinity In-Network - Tier 3"), and showing
 * only one half once they land would misdescribe the network the other half
 * names.
 */
const toNetworkLabel = (office: HcbbOffice): string => {
  const network = orEmptyValue(office.networkName);
  const tier = orEmptyValue(office.tier);

  if (network === EMPTY_VALUE) return tier;
  if (tier === EMPTY_VALUE) return network;

  return `${network} - ${tier}`;
};

const HcbbOfficeRow = ({ office }: { office: HcbbOffice }) => {
  const networkLabel = toNetworkLabel(office);

  return (
    <TableRow>
      <TableCell>
        <p className="font-medium text-gray-800">
          <HcbbTruncatedText text={orEmptyValue(office.displayName)} />
        </p>
        <p className="text-sm text-gray-600">{orEmptyValue(office.address)}</p>
        <p className="text-sm text-gray-600">{orEmptyValue(office.phone)}</p>
      </TableCell>
      <TableCell>
        {networkLabel === EMPTY_VALUE ? (
          networkLabel
        ) : (
          <Badge className="rounded-full bg-green-600 font-light text-white">
            {networkLabel}
          </Badge>
        )}
      </TableCell>
      <TableCell>{orEmptyValue(office.distance)}</TableCell>
    </TableRow>
  );
};
